import { FC, useState, useEffect } from "react";
import cn from "classnames";
import { useChart } from "@stores/chart.store";
import { ChartResolution } from "@api/AviproductionChartApi";
import { usePools } from "@stores/pools.store";
import { useSwap } from "@stores/swap.store";

// Time constants in seconds
const TIME = {
    MINUTE: 60,
    FIVE_MINUTES: 5 * 60,
    FIFTEEN_MINUTES: 15 * 60,
    FORTY_FIVE_MINUTES: 45 * 60,
    HOUR: 60 * 60,
    DAY: 60 * 60 * 24,
    WEEK: 60 * 60 * 24 * 7,
    MONTH: 60 * 60 * 24 * 30,
    THREE_MONTHS: 60 * 60 * 24 * 90,
    HALF_YEAR: 60 * 60 * 24 * 180,
    YEAR: 60 * 60 * 24 * 365,
} as const;

// Configuration for different time periods
export interface TimePeriodConfig {
    label: string;
    visibleRange: number; // in seconds - initial view range for chart
    datasetSize: number; // in seconds - period from which data is fetched from API
    resolution: ChartResolution;
    tickMarkFormatter?: (time: number) => string;
    localization: {
        dateFormat?: string;
        timeFormatter: (businessDayOrTimestamp: number | { time: number }) => string;
    };
}

export const timePeriods: TimePeriodConfig[] = [
    {
        label: "1m",
        visibleRange: TIME.FORTY_FIVE_MINUTES,
        datasetSize: TIME.WEEK,
        resolution: ChartResolution.ONE_MINUTE,
        tickMarkFormatter: (time) => {
            const date = new Date(time * 1000);
            return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
        },
        localization: {
            dateFormat: "",
            timeFormatter: (businessDayOrTimestamp) => {
                const time =
                    typeof businessDayOrTimestamp === "number" ? businessDayOrTimestamp : businessDayOrTimestamp.time;
                const date = new Date(time * 1000);
                return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
            },
        },
    },
    {
        label: "5m",
        visibleRange: TIME.FORTY_FIVE_MINUTES,
        datasetSize: TIME.WEEK,
        resolution: ChartResolution.FIVE_MINUTES,
        tickMarkFormatter: (time) => {
            const date = new Date(time * 1000);
            return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
        },
        localization: {
            dateFormat: "",
            timeFormatter: (businessDayOrTimestamp) => {
                const time =
                    typeof businessDayOrTimestamp === "number" ? businessDayOrTimestamp : businessDayOrTimestamp.time;
                const date = new Date(time * 1000);
                return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
            },
        },
    },
    {
        label: "1H",
        visibleRange: TIME.DAY,
        datasetSize: TIME.MONTH,
        resolution: ChartResolution.ONE_HOUR,
        tickMarkFormatter: (time) => {
            const date = new Date(time * 1000);
            return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
        },
        localization: {
            dateFormat: "MM/dd",
            timeFormatter: (businessDayOrTimestamp) => {
                const time =
                    typeof businessDayOrTimestamp === "number" ? businessDayOrTimestamp : businessDayOrTimestamp.time;
                const date = new Date(time * 1000);
                return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
            },
        },
    },
    {
        label: "1D",
        visibleRange: TIME.WEEK,
        datasetSize: TIME.THREE_MONTHS,
        resolution: ChartResolution.ONE_HOUR,
        tickMarkFormatter: (time) => {
            const date = new Date(time * 1000);
            return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
        },
        localization: {
            dateFormat: "MM/dd",
            timeFormatter: (businessDayOrTimestamp) => {
                const time =
                    typeof businessDayOrTimestamp === "number" ? businessDayOrTimestamp : businessDayOrTimestamp.time;
                const date = new Date(time * 1000);
                return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
            },
        },
    },
    {
        label: "1W",
        visibleRange: TIME.MONTH,
        datasetSize: TIME.THREE_MONTHS,
        resolution: ChartResolution.ONE_DAY,
        tickMarkFormatter: (time) => {
            const date = new Date(time * 1000);
            return date.toLocaleDateString("en-US", { weekday: "short" });
        },
        localization: {
            dateFormat: "MM/dd",
            timeFormatter: (businessDayOrTimestamp) => {
                const time =
                    typeof businessDayOrTimestamp === "number" ? businessDayOrTimestamp : businessDayOrTimestamp.time;
                const date = new Date(time * 1000);
                return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
            },
        },
    },
];

const TimeSwitch: FC = () => {
    const { updateChartData, updateChartOptions } = useChart();
    const { pools } = usePools();
    const { sellAssetInfo, buyAssetInfo, selectedPool } = useSwap();
    const [selectedTimePeriod, setSelectedTimePeriod] = useState<TimePeriodConfig | null>(timePeriods[0]);

    // Initialize chart with the first time period when data is available
    useEffect(() => {
        if (sellAssetInfo.asset && buyAssetInfo.asset && selectedPool && pools) {
            if (!selectedTimePeriod) {
                setSelectedTimePeriod(timePeriods[0]);
            }
            // Trigger initial data fetch
            handleTimeClick(timePeriods[0]);
        }
    }, [sellAssetInfo, buyAssetInfo, selectedPool, pools]);

    // Handle time button click
    const handleTimeClick = (config: TimePeriodConfig) => {
        setSelectedTimePeriod(config);
        if (sellAssetInfo.asset && buyAssetInfo.asset && selectedPool && pools) {
            const poolData = pools.find((pool) => pool.dex === selectedPool);

            if (poolData) {
                const to = Math.floor(Date.now() / 1000);
                const from = to - config.datasetSize;
                const visibleFrom = to - config.visibleRange;

                // Update chart options with time formats from the config
                updateChartOptions({
                    timeScale: {
                        rightOffset: 0,
                        barSpacing: 6,
                        lockVisibleTimeRangeOnResize: false,
                        rightBarStaysOnScroll: true,
                        borderVisible: false,
                        borderColor: "#444444",
                        visible: true,
                        timeVisible: true,
                        secondsVisible: false,
                        tickMarkFormatter: config.tickMarkFormatter,
                    },
                    localization: {
                        dateFormat: config.localization.dateFormat,
                        timeFormatter: config.localization.timeFormatter,
                    },
                });

                // Fetch new chart data with the selected configuration
                updateChartData(
                    poolData,
                    [sellAssetInfo.asset, buyAssetInfo.asset],
                    config.resolution,
                    from, // Use datasetSize for API data fetching
                    to,
                    visibleFrom, // Use visibleRange for chart visibility
                ).catch((error) => {
                    console.error("Error updating chart data:", error);
                });
            }
        }
    };

    return (
        <div className={cn("flex gap-2 items-start")}>
            {timePeriods.map((config) => (
                <button
                    key={config.label}
                    onClick={() => handleTimeClick(config)}
                    className={cn(
                        "rounded-full px-3 py-1 text-sm font-medium",
                        "transition-colors focus:outline-none focus:ring-2",
                        selectedTimePeriod?.label === config.label
                            ? " text-primary bg-card-foreground/20"
                            : "bg-card hover:bg-card-foreground/10",
                        "focus:ring-card-foreground/20",
                    )}
                >
                    <span>{config.label}</span>
                </button>
            ))}
        </div>
    );
};

export default TimeSwitch;
